import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { BuitenlandMaand, BuitenlandOverzicht } from '@fleet/shared';
import { buitenlandInPeriode, euro, fmtDate, landNaam, monthLabel } from '@fleet/shared';
import { AutoLink, PersoonLink } from '../../components/ui/EntiteitLink';
import { InfoTip } from '../../components/ui/InfoTip';
import { Panel } from '../../components/ui/Panel';
import { PeriodeKiezer } from '../../components/ui/PeriodeKiezer';

const MONO = "'Roboto Mono', monospace";

/**
 * De uitdraaiknop, exact zo hoog als de periodechips ernaast.
 *
 * Padding, lettergrootte en radius komen uit `Dropdown`. Maar dat was niet genoeg,
 * en daar zat de laatste pixel: die trigger is een `<button>` en dit is een `<a>`.
 * Een button erft de regelhoogte van `body` niet — die houdt de browserstandaard
 * `normal` — terwijl een anker de 1,55 van `body` wél overneemt. Bij 12px scheelt
 * dat ruim vier pixels in de hoogte.
 *
 * Vandaar `lineHeight: 'normal'`: niet een getal dat er toevallig goed uitziet, maar
 * dezelfde waarde die de button gebruikt, zodat ze per definitie gelijk zijn.
 */
const UITDRAAI: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 'normal',
  padding: '5px 11px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid #C9BBA8',
  background: '#FFFFFF',
  color: 'var(--color-primary)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

/**
 * Hoe de gekozen periode in een zin heet.
 *
 * Het jaar bij `YTD` komt uit de maanden die meededen en niet uit de klok: "dit jaar"
 * is hier het nieuwste jaar waar data van is. De MTC-factuur komt met een maand
 * vertraging, dus in januari zou de klok naar een leeg 2027 wijzen terwijl december
 * net binnen is.
 */
function periodeNaam(periode: string, maanden: readonly string[]): string {
  if (periode === 'ALL') return 'de hele periode';
  if (periode === 'YTD') return maanden[0]?.slice(0, 4) ?? 'dit jaar';
  return monthLabel(periode);
}

/**
 * Waar over de grens getankt en geladen is, over de gekozen periode.
 *
 * Hier staat alleen wat vaststaat: de plaats is geen Nederlandse woonplaats, of hij
 * was er een en iemand heeft in het actiecentrum gezegd dat het tóch buitenland was.
 * Openstaande twijfel staat daar en niet hier — anders zou deze lijst iets beweren
 * wat nog een vraag is, en de uitdraai eronder gaat naar de administratie.
 *
 * De periodechip stuurt allebei aan: wat je ziet en wat de uitdraai bevat. Eén keuze,
 * geen tweede keuzemenu naast de knop dat er anders bij zou kunnen staan.
 *
 * Hij begint op een maand en niet op *Alle*, net als de andere kaarten van het
 * dashboard: de gewone beweging is de nieuwste maand nakijken en uitdraaien. *Dit
 * jaar* is er sindsdien één klik bij, en dat was precies wat er niet kon — je moest
 * zeven maanden aanklikken en de bedragen zelf optellen.
 */
export function BuitenlandPanel({
  overzicht,
  exportUrl,
}: {
  overzicht: BuitenlandOverzicht;
  exportUrl: (periode: string) => string;
}) {
  const { maanden } = overzicht;
  // De nieuwste maand waar iets in staat, en anders gewoon de nieuwste: openen op
  // een lege maand terwijl er verderop wél iets is, verbergt precies waar het om gaat.
  const standaard =
    maanden.find((m) => m.transacties.length > 0)?.maand ?? maanden[0]?.maand ?? null;
  const [gekozen, setGekozen] = useState<string | null>(null);
  const periode = gekozen ?? standaard;
  const view = periode ? buitenlandInPeriode(maanden, periode) : null;
  const heeftTransacties = (view?.transacties.length ?? 0) > 0;
  const naam = view ? periodeNaam(view.periode, view.maanden) : '';

  // De maanden waar de lijst uit opgebouwd wordt, oudste eerst — dezelfde volgorde als
  // `view.transacties`, maar met de maandgrens er nog in. Bij meer dan één maand komt
  // daar een kopje boven, want 46 regels over dertien maanden achter elkaar leest niet.
  const perMaand: BuitenlandMaand[] = view
    ? view.maanden
        .map((m) => maanden.find((x) => x.maand === m))
        .filter((m): m is BuitenlandMaand => m != null && m.transacties.length > 0)
    : [];
  const meerdereMaanden = perMaand.length > 1;

  // Hoeveel van de transacties in déze periode een plaats hadden, en dus of dit een
  // volledig beeld is. Het MTC-transactieoverzicht liet de locatie bij laadsessies
  // leeg en de MoveMove-factuur doet dat niet, dus over meer maanden smeert dit het
  // verschil uit — wie op *Alle* klikt vraagt daarom, en wie wil weten welke maand het
  // gat veroorzaakt kiest die maand.
  const totaal = view ? view.dekking.metPlaats + view.dekking.zonderPlaats : 0;
  const dekkingPct = totaal > 0 ? Math.round((view!.dekking.metPlaats / totaal) * 100) : 100;

  return (
    <Panel style={{ padding: '18px 22px 16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111111' }}>
            Getankt in het buitenland
          </span>
          {/* Het percentage staat in de toelichting en niet als losse regel onder de
              lijst: het is een voorbehoud bij wat je ziet, geen bevinding. */}
          <InfoTip
            uitlijning="links"
            tekst={
              // De uitleg noemt de landcode eerst, want dat is de harde bron. Alleen
              // waar die ontbreekt — en bij laadsessies ontbreekt hij altijd — hangt
              // het aan de plaatsnaam, en dan is de dekking een voorbehoud.
              totaal === 0
                ? `Staat er een landcode op de factuur, dan volgen we die. Bij laadsessies staat hij er nooit; dan leiden we het af uit de plaatsnaam. In ${naam || 'deze periode'} staan nog geen transacties.`
                : dekkingPct === 100
                  ? `Staat er een landcode op de factuur, dan volgen we die — die is hard. Bij laadsessies staat hij er nooit; dan leiden we het af uit de plaatsnaam. Van alle ${totaal.toLocaleString('nl-NL')} transacties in ${naam} is de plaats bekend, dus dit beeld is compleet.`
                  : `Staat er een landcode op de factuur, dan volgen we die — die is hard. Bij laadsessies staat hij er nooit; dan leiden we het af uit de plaatsnaam. Bij ${dekkingPct}% van de ${totaal.toLocaleString('nl-NL')} transacties in ${naam} staat een plaats — de rest zijn laadsessies uit het oude MTC-overzicht. Daar kan buitenland tussen zitten dat we niet zien.`
            }
          />
        </span>

        {/* Uitdraai links, periodekiezer rechts: wit vóór blauw, zoals overal in de
            app. Ze staan naast elkaar omdat ze bij elkaar horen — de periode die je
            hier kiest is de periode die je downloadt. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Een gewone link en geen knop: het is een download, dus rechtsklikken en
              "opslaan als" hoort te werken. Zonder transacties valt er niets te halen
              en is hij grijs — anders levert klikken een 404 op. */}
          {view && heeftTransacties ? (
            <a href={exportUrl(view.periode)} style={UITDRAAI}>
              Excel-uitdraai
            </a>
          ) : (
            <span
              title="Geen transacties in deze periode om uit te draaien"
              style={{
                ...UITDRAAI,
                border: '1px solid #E3D9CC',
                color: '#C9BBA8',
                cursor: 'not-allowed',
              }}
            >
              Excel-uitdraai
            </span>
          )}
          {periode && (
            // Dezelfde chips als bij de andere kaarten, met dezelfde tekens: `ALL`,
            // `YTD` of `JJJJ-MM`. De maanden oplopend, want zo staan ze in de
            // dropdown van elke andere kaart; `overzicht.maanden` is aflopend.
            <PeriodeKiezer
              months={maanden.map((m) => m.maand).sort()}
              period={periode}
              onPeriod={setGekozen}
            />
          )}
        </span>
      </div>

      {view && heeftTransacties ? (
        <>
          <div style={{ fontSize: 12.5, color: '#777777', margin: '12px 0 2px' }}>
            {view.transacties.length} {view.transacties.length === 1 ? 'transactie' : 'transacties'}{' '}
            in {naam} · {euro(view.bedragExcl)} excl. btw
          </div>

          {perMaand.map((m) => (
            <div key={m.maand}>
              {/* Alleen als er meer dan één maand in beeld is: bij één maand staat de
                  maand al in de regel erboven, en dan is dit een dubbele kop. */}
              {meerdereMaanden && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '12px 0 4px',
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: '#AAA093',
                  }}
                >
                  <span>{monthLabel(m.maand)}</span>
                  <span style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
                    {euro(m.bedragExcl)}
                  </span>
                </div>
              )}

              {m.transacties.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(72px, auto) minmax(0, 1fr) auto',
                    alignItems: 'center',
                    gap: 12,
                    padding: '7px 0',
                    fontSize: 12.5,
                    borderTop: '1px solid #F0E8DC',
                  }}
                >
                  <span
                    style={{
                      color: '#777777',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {fmtDate(t.datum)}
                    {t.tijd && <span style={{ color: '#AAA093' }}> {t.tijd}</span>}
                  </span>

                  <AutoLink kenteken={t.kenteken} style={{ fontSize: 11.5 }} />

                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.persoonId && t.persoonNaam ? (
                      <PersoonLink persoonId={t.persoonId} naam={t.persoonNaam} />
                    ) : (
                      <span style={{ fontWeight: 700, color: '#AAA093' }}>
                        {t.persoonNaam ?? 'geen bestuurder'}
                      </span>
                    )}
                    <span style={{ color: '#777777' }}>
                      {' · '}
                      {/* Het land vóór de plaats, en niet erachter: bij DKV noemt de
                          factuur de vestigingsplaats van DKV Nederland in plaats van
                          waar getankt is, dus daar staat een Nederlandse plaatsnaam
                          onder een Franse tankbeurt. Zonder het land ervoor leest die
                          regel als een fout. */}
                      {t.land && (
                        <strong style={{ color: '#8A4029' }}>{landNaam(t.land)} · </strong>
                      )}
                      {t.plaatsRuw || '—'}
                      {' · '}
                      {t.categorie.toLowerCase()}
                    </span>
                    {/* Alleen bij een handmatig besluit, want dan komt het niet uit de
                        plaatsenlijst maar uit een oordeel van een mens. */}
                    {t.bevestigd && (
                      <span style={{ color: '#AAA093' }} title="Door jou als buitenland aangemerkt">
                        {' · bevestigd'}
                      </span>
                    )}
                  </span>

                  <span
                    style={{
                      color: '#111111',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {euro(t.bedragExcl)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </>
      ) : (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: '#2A7050', fontWeight: 700 }}>
          {!view
            ? 'Nog geen transacties ingelezen.'
            : view.periode === 'ALL'
              ? 'Nooit over de grens getankt.'
              : `Niet over de grens getankt in ${naam}.`}
        </p>
      )}
    </Panel>
  );
}
